import React, { useState, useEffect } from "react";
import { Input } from "src/components/ui/Input";
import { Textarea } from "src/components/ui/TextArea";
import { Label } from "src/components/ui/Label";
import ScrollToTop from "src/components/ScrollToTop";
import FileUploadCV from "src/components/ui/FileUploadCV";
import { tutorOnboardingProfileSchema } from "src/services/errorHandler";
import { useTutorOnboardingStore } from "src/store/tutorOnboardingStore";
import { validateCVFile } from "src/utils/storageUtils";
import { uploadCVFile } from "src/services/cvUploadService";
import { useAuth } from "src/context/AuthContext";

interface TutorOnboardingStepFourProps {
  setStepFourCompleteHandler?: (handler: () => Promise<string | boolean>) => void;
}

const TutorOnboardingStepFour: React.FC<TutorOnboardingStepFourProps> = ({
  setStepFourCompleteHandler,
}) => {
  // Get all state and setters from Zustand store
  const firstName = useTutorOnboardingStore((state) => state.firstName);
  const setFirstName = useTutorOnboardingStore((state) => state.setFirstName);
  const lastName = useTutorOnboardingStore((state) => state.lastName);
  const setLastName = useTutorOnboardingStore((state) => state.setLastName);
  const bio = useTutorOnboardingStore((state) => state.bio);
  const setBio = useTutorOnboardingStore((state) => state.setBio);
  const hourlyRate = useTutorOnboardingStore((state) => state.hourlyRate);
  const setHourlyRate = useTutorOnboardingStore((state) => state.setHourlyRate);
  const cvFile = useTutorOnboardingStore((state) => state.cvFile);
  const setCvFile = useTutorOnboardingStore((state) => state.setCvFile);
  const cvFilePath = useTutorOnboardingStore((state) => state.cvFilePath);
  const setCvFilePath = useTutorOnboardingStore((state) => state.setCvFilePath);

  const [cvStatus, setCvStatus] = useState<"idle" | "ready" | "uploading" | "success" | "error" | "saving" | "saved">("idle");
  const [cvError, setCvError] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  // Get user from auth context
  const { user } = useAuth();

  React.useEffect(() => {
    console.log("[DEBUG] Rendered: touched=", touched, "errors=", errors);
    console.log("[DEBUG] Store values:", { firstName, lastName, bio, hourlyRate, cvFile });
  });

  React.useEffect(() => {
    console.log("[DEBUG] Mounted: touched=", touched, "errors=", errors);
    console.log("[DEBUG] Initial store values:", { firstName, lastName, bio, hourlyRate, cvFile });
  }, []);

  // Validate all fields using current store state
  const validate = (fieldsToTouch?: string[], touchedOverride?: Record<string, boolean>) => {
    // Use the override if provided, otherwise use current touched state
    const currentTouched = touchedOverride || touched;
    
    // Optionally mark fields as touched
    let newTouched = currentTouched;
    if (fieldsToTouch) {
      newTouched = { ...currentTouched, ...Object.fromEntries(fieldsToTouch.map(f => [f, true])) };
      setTouched(newTouched);
    }

    // Get fresh values from store for validation
    const storeState = useTutorOnboardingStore.getState();
    const values = {
      firstName: storeState.firstName,
      lastName: storeState.lastName,
      bio: storeState.bio,
      hourlyRate: storeState.hourlyRate,
      cvFile: storeState.cvFile,
    };
    console.log("[DEBUG] Validating values:", values);
    console.log("[DEBUG] Current touched state:", newTouched);
    console.log("[DEBUG] Current submitted state:", submitted);
    
    const result = tutorOnboardingProfileSchema.safeParse(values);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        console.log("[DEBUG] Validation error:", err.path, err.message);
        if (err.path[0]) fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      console.log("[DEBUG] setErrors in validate: ", fieldErrors);
      return false;
    }
    setErrors({});
    console.log("[DEBUG] setErrors in validate: {} (no errors)");
    return true;
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => {
      const newTouched = { ...prev, [field]: true };
      // Use setTimeout to ensure validation runs after state update
      setTimeout(() => validate(undefined, newTouched), 0);
      return newTouched;
    });
  };

  // Handle CV file select: only validate and store, do not upload yet
  const handleFileSelect = (file: File) => {
    setSubmitted(false);
    console.log("Selected file:", file, "error:", (file as any).error, "errorMessage:", (file as any).errorMessage);
    if ((file as any).error) {
      setCvStatus("error");
      setCvError((file as any).errorMessage || "Upload failed. Please try again or use a different file.");
      setCvFile(null);
      return;
    }
    setCvStatus("ready");
    setCvError("");
    setCvFile(file);
    // Clear cvFile error when a valid file is selected
    if (errors.cvFile) {
      setErrors(prev => ({ ...prev, cvFile: "" }));
    }
  };

  const handleFileRemove = () => {
    setSubmitted(false);
    setCvFile(null);
    setCvStatus("idle");
    setCvError("");
  };

  // Expose a handler for the parent to call when 'Complete' is clicked
  useEffect(() => {
    if (setStepFourCompleteHandler) {
      setStepFourCompleteHandler(async () => {
        setSubmitted(true);
        const allFields = ["firstName", "lastName", "bio", "hourlyRate", "cvFile"];
        
        // Get fresh values from store at submission time
        const storeState = useTutorOnboardingStore.getState();
        console.log("[DEBUG][SUBMIT] Fresh store state:", {
          firstName: storeState.firstName,
          lastName: storeState.lastName,
          bio: storeState.bio,
          hourlyRate: storeState.hourlyRate,
          cvFile: storeState.cvFile,
          cvFilePath: storeState.cvFilePath,
        });
        
        console.log("[DEBUG][SUBMIT] Component state values:", {
          firstName,
          lastName,
          bio,
          hourlyRate,
          cvFile,
          cvFilePath,
        });
        
        // Create touched override to ensure all fields are marked as touched for validation
        const touchedOverride = Object.fromEntries(allFields.map(f => [f, true]));
        
        // Use fresh store values for validation
        const validationValues = {
          firstName: storeState.firstName,
          lastName: storeState.lastName,
          bio: storeState.bio,
          hourlyRate: storeState.hourlyRate,
          cvFile: storeState.cvFile,
        };
        
        console.log("[DEBUG][SUBMIT] Validating with fresh values:", validationValues);
        console.log("[DEBUG][SUBMIT] cvFile type check:", {
          cvFile: validationValues.cvFile,
          isFile: validationValues.cvFile instanceof File,
          type: typeof validationValues.cvFile,
          constructor: validationValues.cvFile?.constructor?.name
        });
        const result = tutorOnboardingProfileSchema.safeParse(validationValues);
        if (!result.success) {
          const fieldErrors: Record<string, string> = {};
          result.error.errors.forEach((err) => {
            console.log("[DEBUG][SUBMIT] Validation error:", err.path, err.message);
            if (err.path[0]) fieldErrors[err.path[0]] = err.message;
          });
          setErrors(fieldErrors);
          setTouched(touchedOverride);
          console.log("[DEBUG][SUBMIT] Validation failed with errors:", fieldErrors);
          return false;
        }
        
        console.log("[DEBUG][SUBMIT] Validation passed!");
        let uploadedPath = storeState.cvFilePath;
        if (storeState.cvFile && user?.id) {
          setCvStatus("uploading");
          setCvError("");
          try {
            // Use the service role CV upload service
            console.log("[DEBUG][SUBMIT] Using service role CV upload for:", storeState.cvFile.name);
            const uploadResult = await uploadCVFile(storeState.cvFile, user.id);
            
            if (!uploadResult.success) {
              throw new Error(uploadResult.error || "Upload failed");
            }
            
            uploadedPath = uploadResult.filePath!;
            console.log("[DEBUG][SUBMIT] File uploaded successfully via service role:", uploadedPath);
            
            // Update the store with the new file path
            setCvFilePath(uploadedPath);
            setCvStatus("success");
            
            // Also update the store directly to ensure it's saved
            useTutorOnboardingStore.getState().setCvFilePath(uploadedPath);
            console.log("[DEBUG][SUBMIT] Updated store cvFilePath to:", uploadedPath);
          } catch (err) {
            console.error("[DEBUG][SUBMIT] Service role upload failed:", err);
            setCvStatus("error");
            setCvError(err?.message || "Upload failed. Please try again.");
            return false;
          }
        }
        
        console.log("[DEBUG][SUBMIT] Returning uploaded path:", uploadedPath);
        return uploadedPath || true;
      });
    }
    // Only run on mount
    // eslint-disable-next-line
  }, []);

  // Field change handlers to reset submitted state and clear field-specific errors
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubmitted(false);
    setFirstName(e.target.value);
    // Clear field-specific error when user starts typing
    if (errors.firstName) {
      setErrors(prev => ({ ...prev, firstName: "" }));
    }
  };
  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubmitted(false);
    setLastName(e.target.value);
    // Clear field-specific error when user starts typing
    if (errors.lastName) {
      setErrors(prev => ({ ...prev, lastName: "" }));
    }
  };
  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSubmitted(false);
    setBio(e.target.value);
    // Clear field-specific error when user starts typing
    if (errors.bio) {
      setErrors(prev => ({ ...prev, bio: "" }));
    }
  };
  const handleHourlyRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSubmitted(false);
    setHourlyRate(e.target.value);
    // Clear field-specific error when user starts typing
    if (errors.hourlyRate) {
      setErrors(prev => ({ ...prev, hourlyRate: "" }));
    }
  };

  return (
    <>
      <ScrollToTop />
      <form className="space-y-6" noValidate>
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>
            Tell us about yourself
          </h2>
          <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            This information will be displayed on your public profile.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={handleFirstNameChange}
              onBlur={() => handleBlur("firstName")}
              placeholder="John"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              required
            />
            {(touched.firstName || submitted) && errors.firstName && (
              <div className="text-sm text-red-600 mt-1">{errors.firstName}</div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={handleLastNameChange}
              onBlur={() => handleBlur("lastName")}
              placeholder="Doe"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              required
            />
            {(touched.lastName || submitted) && errors.lastName && (
              <div className="text-sm text-red-600 mt-1">{errors.lastName}</div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
            Bio <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="bio"
            value={bio}
            onChange={handleBioChange}
            onBlur={() => handleBlur("bio")}
            placeholder="Tell students about your background, teaching style, and expertise..."
            className="min-h-[120px] w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            required
          />
          {(touched.bio || submitted) && errors.bio && (
            <div className="text-sm text-red-600 mt-1">{errors.bio}</div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700 mb-1">
            Hourly Rate ($) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="hourlyRate"
            type="number"
            value={hourlyRate}
            onChange={handleHourlyRateChange}
            onBlur={() => handleBlur("hourlyRate")}
            placeholder="45"
            className="max-w-[200px] w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            required
          />
          {(touched.hourlyRate || submitted) && errors.hourlyRate && (
            <div className="text-sm text-red-600 mt-1">{errors.hourlyRate}</div>
          )}
        </div>

        {/* CV Upload Section */}
        <div className="space-y-2">
          <Label className="block text-sm font-medium text-gray-700 mb-1">
            Upload CV <span className="text-red-500">*</span>
          </Label>
          <FileUploadCV
            buttonText={
              cvStatus === "uploading"
                ? "Uploading..."
                : cvStatus === "success"
                ? "File uploaded successfully"
                : cvStatus === "saving"
                ? "Saving to database..."
                : cvStatus === "saved"
                ? "Saved!"
                : "Upload or drag and drop your CV"
            }
            required={true}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            fileName={cvFile ? cvFile.name : undefined}
            showSelectedFile={!!cvFile && cvStatus === "ready"}
            status={
              cvStatus === "uploading"
                ? "uploading"
                : cvStatus === "error"
                ? "error"
                : cvStatus === "success"
                ? "success"
                : undefined
            }
            errorMessage={cvError}
          />
          {(touched.cvFile || submitted) && errors.cvFile && (
            <div className="text-sm text-red-600 mt-1">{errors.cvFile}</div>
          )}
        </div>
      </form>
    </>
  );
};

export default TutorOnboardingStepFour;